import React, { useState } from 'react';
import {
  Alert, Box, Checkbox, Collapse, Divider, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { MAX_PARALLEL_TREES, iperfSettingsAnchorOptimize, iperfSettingsEmpty } from '../../utils/iperfScheduleUtils';

export const getIperfCoverageLabels = (reverse) => ({
  hop: {
    title: reverse ? 'Parent → child' : 'Child → parent',
    subtitle: reverse
      ? 'One test per link, traffic from parent toward each child'
      : 'One test per link, every device toward its parent',
  },
  e2e: {
    title: reverse ? 'CAP → leaf' : 'Leaf → CAP',
    subtitle: reverse
      ? 'Deepest leaf in each subtree, traffic from CAP'
      : 'Deepest leaf in each subtree, end-to-end toward CAP',
  },
  allToCap: {
    title: reverse ? 'CAP → every device' : 'Every device → CAP',
    subtitle: reverse
      ? 'CAP sends to each managed device in the tree'
      : 'Each managed device sends directly to the CAP',
  },
});

/**
 * The settings of an iperf test: timing and bandwidth, traffic direction and
 * what to measure. Used by the Iperf tab and by iperf schedules, so a
 * scheduled test is configured exactly like one started by hand.
 *
 * @param {object} props
 * @param {object} props.value { duration, protocol, udpBandwidth, includeHop, includeE2E, includeAllToCap, reverseDirection }
 * @param {(patch:object) => void} props.onChange
 * @param {boolean} [props.timingOpen] start with timing expanded
 * @param {number} [props.treeCount] CAP trees selected (parallelism only matters above 1; null = unknown)
 */
export default function IperfTestSettingsFields({ value, onChange, timingOpen = false, treeCount = null }) {
  const [showTiming, setShowTiming] = useState(timingOpen);
  const {
    duration, protocol, udpBandwidth, udpBandwidthAuto, includeHop, includeE2E, includeAllToCap, reverseDirection,
  } = value;
  const parallelTrees = value.parallelTrees || 1;
  const singleTree = treeCount !== null && treeCount <= 1;
  const udp = protocol === 'UDP';
  const labels = getIperfCoverageLabels(reverseDirection);

  return (
    <Stack spacing={2}>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          onClick={() => setShowTiming((v) => !v)}
          sx={{ cursor: 'pointer', userSelect: 'none', mb: showTiming ? 1 : 0 }}
        >
          <IconButton size="small" tabIndex={-1}>
            {showTiming ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Run timing &amp; bandwidth
          </Typography>
          {!showTiming && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              — {duration}s · {protocol}
              {udp ? (udpBandwidthAuto ? ' · auto rate' : ` · ${udpBandwidth} Mbps`) : ''}
            </Typography>
          )}
        </Stack>
        <Collapse in={showTiming}>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 4.5, pt: 0.5 }}>
            <TextField
              size="small"
              label="Duration (s)"
              type="number"
              value={duration}
              onChange={(e) => onChange({ duration: Number(e.target.value) })}
              sx={{ width: 120 }}
            />
            <FormControl size="small" sx={{ width: 100 }}>
              <InputLabel>Protocol</InputLabel>
              <Select value={protocol} onChange={(e) => onChange({ protocol: e.target.value })} label="Protocol">
                <MenuItem value="UDP">UDP</MenuItem>
                <MenuItem value="TCP">TCP</MenuItem>
              </Select>
            </FormControl>
            <Tooltip
              title="Each UDP test offers a little more than the link under test should carry, from its reported MCS, streams and channel width (the slowest hop for end-to-end and every-device tests). Measures the link's capacity and the loss at that load."
              placement="top-start"
            >
              <FormControlLabel
                disabled={!udp}
                control={<Switch size="small" checked={udp && Boolean(udpBandwidthAuto)} onChange={(e) => onChange({ udpBandwidthAuto: e.target.checked })} />}
                label={<Typography variant="body2">Auto rate (from link)</Typography>}
              />
            </Tooltip>
            <TextField
              size="small"
              label={udpBandwidthAuto ? 'Fallback (Mbps)' : 'UDP BW (Mbps)'}
              helperText={udp && udpBandwidthAuto ? 'For links with no rate report' : undefined}
              type="number"
              value={udpBandwidth}
              onChange={(e) => onChange({ udpBandwidth: Number(e.target.value) })}
              disabled={!udp}
              sx={{ width: 170 }}
            />
          </Stack>
        </Collapse>
      </Box>
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ pl: 4.5 }}>
          <FormControl size="small" sx={{ width: 170 }} disabled={singleTree}>
            <InputLabel>Trees in parallel</InputLabel>
            <Select
              label="Trees in parallel"
              value={singleTree ? 1 : parallelTrees}
              onChange={(e) => onChange({ parallelTrees: Number(e.target.value) })}
            >
              {Array.from({ length: MAX_PARALLEL_TREES }, (_, i) => i + 1).map((n) => (
                <MenuItem key={n} value={n}>{n === 1 ? '1 (one after another)' : `${n} at a time`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
            {singleTree
              ? 'Applies when several CAPs are tested.'
              : 'Each CAP tree is one run; this many run at the same time, the rest wait. Tests inside a tree always run one at a time. Trees on overlapping channels can lower each other\'s results.'}
          </Typography>
        </Stack>
      </Box>
      <Divider />
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
          Traffic direction
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          fullWidth
          value={reverseDirection ? 'downlink' : 'uplink'}
          onChange={(_, v) => {
            if (v) onChange({ reverseDirection: v === 'downlink' });
          }}
        >
          <ToggleButton value="uplink" sx={{ textTransform: 'none', py: 0.75 }}>
            <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.75 }} />
            Toward CAP
          </ToggleButton>
          <ToggleButton value="downlink" sx={{ textTransform: 'none', py: 0.75 }}>
            <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.75 }} />
            From CAP
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {reverseDirection
            ? 'Parent → child, CAP → leaf, CAP → every device (downlink / server on devices).'
            : 'Child → parent, leaf → CAP, every device → CAP (default uplink).'}
        </Typography>
      </Paper>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
          What to measure
        </Typography>
        <Stack spacing={1}>
          {[
            { key: 'includeHop', checked: includeHop, meta: labels.hop },
            { key: 'includeE2E', checked: includeE2E, meta: labels.e2e },
            { key: 'includeAllToCap', checked: includeAllToCap, meta: labels.allToCap },
          ].map(({ key, checked, meta }) => (
            <Box
              key={key}
              onClick={() => onChange({ [key]: !checked })}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1.25,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: checked ? 'primary.main' : 'divider',
                bgcolor: checked ? 'primary.50' : 'background.paper',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
              }}
            >
              <Checkbox
                size="small"
                checked={checked}
                onChange={(e) => {
                  e.stopPropagation();
                  onChange({ [key]: e.target.checked });
                }}
                sx={{ pt: 0.25 }}
              />
              <Box>
                <Typography variant="body2" fontWeight={600}>{meta.title}</Typography>
                <Typography variant="caption" color="text.secondary">{meta.subtitle}</Typography>
              </Box>
            </Box>
          ))}
        </Stack>
        {!iperfSettingsEmpty(value) && !iperfSettingsAnchorOptimize(value) && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5, mt: 1 }}>
            <strong>Note:</strong> Without {labels.hop.title} (hop) tests this run will <strong>not</strong> be used by
            Optimize: hop tests measure each link the optimizer can change. End-to-end tests are optional; all-to-CAP
            results alone cannot show which hop is slow.
          </Alert>
        )}
      </Box>
    </Stack>
  );
}
